import { Grid } from '@mui/material';
import { WithStyles, useTheme, withStyles } from '@mui/styles';
import { SnackbarProvider } from '@tassat/text';
import { browserSessionStore } from '@tassat/utility';
import React from 'react';
import { Redirect } from 'react-router-dom';

import GlobalHeader from 'Containers/globalHeader';
import { FeaturesContext } from 'Lib/context/featuresContext';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getClientFirm } from 'Selectors/firms';
import ExportHandler from 'SharedComponents/exportHandler/exportHandler';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';
import AppStateModals from 'SharedComponents/modals/AppStateModals';
import Sidebar from 'SharedComponents/sidebar';

interface IClientPageProps extends WithStyles<typeof muiStyles>{
    children?: React.ReactNode;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    shrink: {
        flexShrink: 1,
        flexBasis: 'auto'
    },
    wrapper: {
        height: '100%',
        overflowY: 'auto' as const
    },
    sidebar: {
        flexBasis: '100px',
        minWidth: '100px',
        maxWidth: '100px'
    },
    spacer: {
        width: '1rem',
        backgroundColor: theme?.app?.applicationColors.enumColors.greyLighter ||
            '#CFCFCF',
        boxShadow: 'inset -0.8rem 0px 0.8rem -1rem black'
    },
    mainContent: {
        minHeight: '100%',
        flexGrow: 1,
        maxWidth: '100%'
    }
});

export function ClientPage (props: IClientPageProps) {
    const {
        classes,
        children
    } = props;

    const firm = useAppSelector(getClientFirm);

    const muiTheme = useTheme<ApplicationMuiTheme>();
    const [features] = useFeatures();

    React.useEffect(() => {
        window.addEventListener('beforeunload', () => {
            browserSessionStore.setItem('browser_refresh', 'true');
        });
    }, []);

    const cssVariables = React.useMemo(() => {
        const variables = {};
        if (!muiTheme?.app) return;

        variables['--client-color-primary'] = muiTheme.app.clientTheme.colors.primaryColor;
        variables['--client-color-primary-light'] = muiTheme.app.clientTheme.colors.primaryColorLight;
        variables['--client-color-primary-dark'] = muiTheme.app.clientTheme.colors.primaryColorDark;
        variables['--client-color-highlight-1'] = muiTheme.app.clientTheme.colors.highlightColor1;
        variables['--client-color-highlight-2'] = muiTheme.app.clientTheme.colors.highlightColor2;
        variables['--client-color-highlight-3'] = muiTheme.app.clientTheme.colors.highlightColor3;
        variables['--client-color-button-primary-lighter'] = muiTheme.app.clientTheme.colors.buttons.primaryColorLighter;
        variables['--application-color-background-primary'] = muiTheme.app.applicationColors.backgroundPrimary;
        variables['--application-color-background-tos'] = muiTheme.app.applicationColors.backgroundTOS;
        variables['--application-color-disabled-item'] = muiTheme.app.applicationColors.disabledItemColor;
        variables['--application-color-black'] = muiTheme.app.applicationColors.enumColors.black;
        variables['--application-color-red'] = muiTheme.app.applicationColors.enumColors.red;
        variables['--application-color-grey'] = muiTheme.app.applicationColors.enumColors.grey;
        variables['--application-color-grey-dark'] = muiTheme.app.applicationColors.enumColors.greyDark;
        variables['--application-color-grey-light'] = muiTheme.app.applicationColors.enumColors.greyLight;
        variables['--application-color-grey-lighter'] = muiTheme.app.applicationColors.enumColors.greyLighter;
        variables['--application-color-off-white'] = muiTheme.app.applicationColors.enumColors.offWhite;
        variables['--application-color-agGrid-lockedRow'] = muiTheme.app.applicationColors.agGridHover.lockedRow;
        variables['--application-color-agGrid-pendingRow'] = muiTheme.app.applicationColors.agGridHover.pendingRow;
        variables['--application-color-agGrid-nthChild'] = muiTheme.app.applicationColors.agGridHover.nthChild;
        variables['--application-color-agGrid-headerCell'] = muiTheme.app.applicationColors.agGridHover.headerCell;

        return variables;
    }, [muiTheme]);

    React.useEffect(() => {
        if (firm.IsClosed || firm.IsLocked) {
            Worker.getInstance()
                .endSession();
        }
    }, [ firm.IsClosed, firm.IsLocked ]);

    if (firm.IsClosed || firm.IsLocked) {
        return (
            <Redirect
                to={{
                    pathname: '/unauthorized_firm',
                    state: { firmState: firm.IsClosed ? 'IsClosed' : firm.IsLocked ? 'IsLocked' : '' }
                }}
            />
        );
    }

    return (
        <ExportHandler>
            <FeaturesContext.Provider value={features}>
                <SnackbarProvider>
                    <Grid
                        className={classes.wrapper}
                        container
                        direction='row'
                        item
                        spacing={0}
                        style={cssVariables}
                        wrap='nowrap'
                    >
                        <Grid
                            className={classes.sidebar}
                            item
                            md={1}
                        >
                            <Sidebar admin={false} />
                        </Grid>
                        {muiTheme?.app?.clientTheme.layoutSettings.useClientGlobalHeader &&
                            <div className={classes.spacer} />}
                        <Grid
                            className={classes.mainContent}
                            container
                            direction='column'
                            item
                            md={11}
                            spacing={0}
                            wrap='nowrap'
                        >
                            {muiTheme?.app?.clientTheme.layoutSettings.useClientGlobalHeader &&
                                <GlobalHeader />}
                            {children}
                        </Grid>
                        <AppStateModals />
                    </Grid>
                </SnackbarProvider>
            </FeaturesContext.Provider>
        </ExportHandler>
    );
}

export default withStyles(muiStyles)(ClientPage);